import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const APP_ENV = (import.meta.env.VITE_APP_ENV as string) || import.meta.env.MODE

/**
 * 旅程の共有リンク（/s/<token>）と、帯同者本人の精算確認（/p/<token>）・Atsume ID の登録（/id/<id_token>）。URL そのものが鍵なので、
 * 外部の計測・エラー収集に渡してはいけない。渡すと、その送信先を見られる人は誰でも開ける。
 */
export function isShareLinkPath(path: string): boolean {
  // React Router は大文字小文字を区別しないので /S/ も共有ページになる
  return /^\/([sp]|id)\//i.test(path)
}

/**
 * 文字列に含まれる共有リンクの token を伏せる。
 * - ページの URL: /s/<token>・/p/<token>
 * - 公開 API の呼び出し: ?token=<token>（公開フォームの token も同じく伏せる）
 */
export function scrubShareToken(text: string): string
export function scrubShareToken(text: string | undefined): string | undefined
export function scrubShareToken(text: string | undefined): string | undefined {
  if (typeof text !== 'string') return text
  return text
    .replace(/\/([sp]|id)\/[^/?#\s"']+/gi, (_, c: string) => `/${c.toLowerCase()}/[token]`)
    .replace(/([?&](?:id_)?token=)[^&#\s"']+/gi, '$1[token]')
}

/** オブジェクトの中の文字列をすべて伏せる（イベント・スパン・録画イベント用）。 */
export function scrubDeep<T>(value: T): T {
  if (typeof value === 'string') return scrubShareToken(value) as T
  if (!value || typeof value !== 'object') return value
  // 深さに上限を置かない（置くとその下に token が残る）。循環参照は一度だけ辿る。
  const seen = new WeakSet<object>()
  const stack: Record<string, unknown>[] = [value as unknown as Record<string, unknown>]
  while (stack.length > 0) {
    const obj = stack.pop()!
    if (seen.has(obj)) continue
    seen.add(obj)
    for (const k of Object.keys(obj)) {
      const v = obj[k]
      if (typeof v === 'string') obj[k] = scrubShareToken(v)
      else if (v && typeof v === 'object') stack.push(v as Record<string, unknown>)
    }
  }
  return value
}

export function initSentry() {
  if (!DSN) return
  // 共有リンクのページでは初期化しない（ページ読み込みの時点で URL が記録されるため）
  if (typeof window !== 'undefined' && isShareLinkPath(window.location.pathname)) return

  Sentry.init({
    dsn: DSN,
    environment: APP_ENV,
    release: import.meta.env.VITE_COMMIT_SHA as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // 録画は beforeSend を通らないので、ここで伏せる。このフックに来るのはパンくず・
        // 通信などの小さなイベント（DOM の変化は来ない）。画面上の共有 URL は
        // data-sentry-mask で伏せ、共有ページを開いたら録画自体を止める。
        beforeAddRecordingEvent: (event) => scrubDeep(event),
      }),
    ],
    tracesSampleRate: APP_ENV === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // アプリ内から /s/ に移動した場合や、公開 API を呼んだ場合の保険
    beforeSend: (event) => scrubDeep(event),
    beforeSendTransaction: (event) => scrubDeep(event),
    beforeBreadcrumb: (breadcrumb) => scrubDeep(breadcrumb),
    beforeSendSpan: (span) => scrubDeep(span),
  })
}

/** 共有リンクのページを開いたら、録画を止める（アプリ内から来た場合）。 */
export function stopReplayForShareLink() {
  try {
    void Sentry.getReplay()?.stop()
  } catch {
    // Sentry を初期化していなければ何もしない
  }
}

export { Sentry }
